import React from 'react';
import styled from '@emotion/styled';
import { color, height, space } from 'styled-system';

import { GENDER_OPTIONS, GENDER_OPTIONS_LABELS } from '../../constants/form';
import type { TitlePrefix } from '../../enums/form';
import { FieldChangeTrigger } from '../../enums/form';
import { useLocalization } from '../../providers/LocaleProvider';
import { flexGrow } from '../../theme/system-utilities';
import type { FieldChangeTriggerHandler } from '../../types/form';
import { getHTMLElementName } from '../../utils/form/name';
import { Flex } from '../Flex';
import { Radio } from '../Radio';
import VisuallyHidden from '../VisuallyHidden';

export interface TitleFieldProps<FieldName extends string = string> {
  name: FieldName;
  onChange: FieldChangeTriggerHandler<FieldName>;
  value?: string;
  disabled?: boolean;
  nameSuffix?: string;
}

const Fieldset = styled(Flex)`
  margin: 0;
  border: 0;
  padding: 0;
`;

const RadioInput = styled(Radio)<any>`
  ${space};
  ${color};
  ${flexGrow};
  ${height};
`;

const TitleField = ({ name, value, disabled, onChange, nameSuffix }: TitleFieldProps) => {
  const selectedTitle = value as TitlePrefix;
  const { t } = useLocalization();
  const handleChange = (newValue: TitlePrefix) =>
    onChange({
      fieldName: name,
      value: newValue,
      trigger: FieldChangeTrigger.Change,
    });

  return (
    <Fieldset as="fieldset" alignItems="center">
      <VisuallyHidden as="legend">{t('h24_title')}</VisuallyHidden>
      {GENDER_OPTIONS.map(genderOption => (
        <RadioInput
          mr="sp_16"
          mb="sp_0"
          flexGrow="0"
          key={genderOption}
          label={t(GENDER_OPTIONS_LABELS.get(genderOption))}
          name={getHTMLElementName(name, nameSuffix)}
          value={genderOption}
          defaultChecked={selectedTitle === genderOption}
          onChange={e => handleChange(e.target.value as TitlePrefix)}
          disabled={disabled}
        />
      ))}
    </Fieldset>
  );
};

export default TitleField;
